import { type Ref, useCallback } from 'react'
import { useIntl } from 'react-intl'
import {
  faCircleCheck,
  faCircleInfo,
  faSpinner,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { clsx } from 'clsx'

import { ColorPicker } from './ColorPicker'
import { TagAutocomplete } from './TagAutocomplete'
import type { TagAutocompleteHandle } from './TagAutocomplete.types'
import { TAG_COLORS } from './tagging.constants'
import type { Tag } from './tagging.types'
import { useTagging } from './useTagging'
import { useOptionalTaggingTableSelection } from './useTaggingTableSelection'

export type EditorDropdownPlacement = 'start' | 'end'

interface EditorDropdownProps {
  placement?: EditorDropdownPlacement
}

export const EditorDropdown = (props: EditorDropdownProps) => {
  const { placement = 'end' } = props
  const intl = useIntl()
  const tagging = useTagging()
  const selection = useOptionalTaggingTableSelection()
  const selectedCount = selection?.selectedCount ?? 1
  const isMutating = tagging.isAddingTag || tagging.isRemovingTag

  const { setInputText, setSelectedColor } = tagging

  const handleAutocompleteSelect = useCallback(
    (tag: Tag) => {
      setInputText(tag.name)
      setSelectedColor(tag.color)
    },
    [setInputText, setSelectedColor],
  )

  if (!tagging.canSubmit && tagging.orgTags.length === 0) {
    return null
  }

  return (
    <div
      className={clsx('EditorDropdown', {
        'EditorDropdown--end': placement === 'end',
        'EditorDropdown--loading': isMutating,
        'EditorDropdown--start': placement === 'start',
      })}
    >
      <div className="EditorDropdown-controls">
        <ColorPicker
          colors={TAG_COLORS}
          onChange={tagging.setSelectedColor}
          selectedColor={tagging.selectedColor}
        />

        <button
          aria-label={intl.formatMessage({ id: 'Tags_confirm_tag' })}
          className={clsx('EditorDropdown-submit', {
            'EditorDropdown-submit--active': tagging.canSubmit && !isMutating,
            'EditorDropdown-submit--loading': isMutating,
          })}
          disabled={!tagging.canSubmit || isMutating}
          onClick={tagging.handleSubmit}
          type="button"
        >
          <FontAwesomeIcon
            icon={isMutating ? faSpinner : faCircleCheck}
            spin={isMutating}
          />
        </button>
      </div>

      {selectedCount > 1 && (
        <div className="EditorDropdown-info">
          <FontAwesomeIcon
            className="EditorDropdown-infoIcon"
            icon={faCircleInfo}
          />
          <span>
            {intl.formatMessage(
              { id: tagging.applyingToCountMessageId },
              { count: selectedCount },
            )}
          </span>
        </div>
      )}

      <TagAutocomplete
        ref={tagging.autocompleteRef as Ref<TagAutocompleteHandle>}
        onFocusInput={() => tagging.inputRef.current?.focus()}
        onSelect={handleAutocompleteSelect}
        orgTags={tagging.orgTags}
        query={tagging.inputText}
      />
    </div>
  )
}
